// TrendSummary.tsx (Demo)
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDemandMetrics,
  getDiscomDrawalScheduleMetrics,
  getTotalDrawalScheduleMetrics,
  getRajasthanDrawalScheduleForecastMetrics,
  // ✅ DEMO: imports from DemoRestApi
} from "../../../Rest_api/DemoRestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";

import { AddDatesWithFileName } from "../../../helpers/helper";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { getTrendMetrics } from "../../Rajasthan/Utility/RestApi";
import { TrendData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];
const UNITS = ["MW", "MWh", "LU"];
const GROUP_BY = [
  "Discom Load",
  "Total Discom Load",
  "RVPN Drawal",
  "Total Load & RVPN Drawal",
];

// ✅ DEMO: anonymised Discom labels/keys
const DISCOM = [
  { label: "Discom 1", key: "Discom 1" },
  { label: "Discom 2", key: "Discom 2" },
  { label: "Discom 3", key: "Discom 3" },
];

const TYPE = ["Schedule", "Drawal"];
const FORECAST_TYPES = ["Actual", "Forecast D+1", "Forecast D+2"];

// ─── Props ────────────────────────────────────────────────────────────────────

interface RajasthanSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: TrendData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RajasthanSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: RajasthanSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  // ── State ──────────────────────────────────────────────────────────────────
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [discomData, setDiscomData] = useState<any[]>([]);
  const [totalDiscomData, setTotalDiscomData] = useState<any[]>([]);
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedForecastType, setSelectedForecastType] = useState<string[]>([
    "Actual",
  ]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const trendDates = DefaultDates[activeTab]?.defaultTrendDates || [];
  const isActualDates =
    trendDates.length > 0 &&
    trendDates.some((d: any) => !d.label?.includes("(Forecast)"));
  const isForecastDates =
    trendDates.length > 0 &&
    (trendDates.some((d: any) => d.label?.includes("(Forecast)")) ||
      selectedForecastType.some((t: string) => t.includes("Forecast")));

  const dateLabel = trendDates
    .filter((d: any) => !d.label?.includes("(Forecast)"))
    .map((d: any) => d.label)
    .join(", ");

  // ── Fetch all 4 APIs in parallel (same as production) ─────────────────────
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      const allDates = trendDates.flatMap((dateRange: any) => {
        const [start, end] = dateRange.value.split(",");
        return [start, end].filter(Boolean);
      });

      if (allDates.length === 0) {
        setSummaryData([]);
        setDiscomData([]);
        setTotalDiscomData([]);
        setForecastData([]);
        setLoading(false);
        return;
      }

      const sorted = allDates.sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      );
      const startDateStr = sorted[0];
      const endDateStr = sorted[sorted.length - 1];

      // ✅ All 4 APIs fired in parallel — same as production
      const [
        rvpnActualRes,
        discomMetricsRes,
        totalDiscomMetricsRes,
        forecastMetricsRes,
      ] = await Promise.all([
        isActualDates
          ? getRajasthanDemandMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isActualDates
          ? getDiscomDrawalScheduleMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isActualDates
          ? getTotalDrawalScheduleMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
        isForecastDates ||
        selectedForecastType.some((t) => t.includes("Forecast"))
          ? getRajasthanDrawalScheduleForecastMetrics(startDateStr, endDateStr)
          : Promise.resolve([]),
      ]);

      setSummaryData(Array.isArray(rvpnActualRes) ? rvpnActualRes : []);
      setDiscomData(Array.isArray(discomMetricsRes) ? discomMetricsRes : []);
      setTotalDiscomData(
        Array.isArray(totalDiscomMetricsRes) ? totalDiscomMetricsRes : [],
      );
      setForecastData(
        Array.isArray(forecastMetricsRes) ? forecastMetricsRes : [],
      );
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setSummaryData([]);
      setDiscomData([]);
      setTotalDiscomData([]);
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  }, [isActualDates, isForecastDates, selectedForecastType, trendDates]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ── Process raw data into chart format ────────────────────────────────────
  useEffect(() => {
    const cf =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    // ── RVPN Drawal ──────────────────────────────────────────────────────────
    if (selectedGroupKeys === "RVPN Drawal") {
      let dataToProcess: any[] = [];

      if (selectedForecastType.includes("Actual") && isActualDates) {
        dataToProcess = summaryData.map((item) => ({
          slot: item.slot,
          slot_number: item.slot,
          slot_with_time:
            item.slot && item.time_string
              ? `${item.slot} (${item.time_string})`
              : item.slot,
          min_drawal: item.min_demand * cf,
          max_drawal: item.max_demand * cf,
          avg_drawal: item.avg_demand * cf,
          mode_drawal_window_formula: item.mode_demand_window_formula * cf,
          date_of_min_drawal: item.date_of_min,
          date_of_max_drawal: item.date_of_max,
          forecast_type: "Actual",
        }));
      }

      if (selectedForecastType.includes("Forecast D+1") && isForecastDates) {
        const t1Data = forecastData
          .filter(
            (item) => item.forecast_type === "T+1" && item.discom === "Total",
          )
          .map((item) => ({
            slot: item.block,
            slot_number: item.block,
            slot_with_time:
              item.block && item.time_string
                ? `${item.block} (${item.time_string})`
                : item.block,
            min_drawal_t1: (item.min_drawal || 0) * cf,
            max_drawal_t1: (item.max_drawal || 0) * cf,
            avg_drawal_t1: (item.avg_drawal || 0) * cf,
            mode_drawal_window_formula_t1:
              (item.mode_drawal_window_formula || 0) * cf,
            date_of_min_drawal_t1: item.date_of_min_drawal,
            date_of_max_drawal_t1: item.date_of_max_drawal,
            forecast_type: "T+1",
          }));

        dataToProcess =
          dataToProcess.length > 0
            ? dataToProcess.map((actual) => {
                const t1 = t1Data.find((t) => t.slot === actual.slot);
                return t1 ? { ...actual, ...t1 } : actual;
              })
            : t1Data;
      }

      if (selectedForecastType.includes("Forecast D+2") && isForecastDates) {
        const t2Data = forecastData
          .filter(
            (item) => item.forecast_type === "T+2" && item.discom === "Total",
          )
          .map((item) => ({
            slot: item.block,
            slot_number: item.block,
            slot_with_time:
              item.block && item.time_string
                ? `${item.block} (${item.time_string})`
                : item.block,
            min_drawal_t2: (item.min_drawal || 0) * cf,
            max_drawal_t2: (item.max_drawal || 0) * cf,
            avg_drawal_t2: (item.avg_drawal || 0) * cf,
            mode_drawal_window_formula_t2:
              (item.mode_drawal_window_formula || 0) * cf,
            date_of_min_drawal_t2: item.date_of_min_drawal,
            date_of_max_drawal_t2: item.date_of_max_drawal,
            forecast_type: "T+2",
          }));

        dataToProcess =
          dataToProcess.length > 0
            ? dataToProcess.map((existing) => {
                const t2 = t2Data.find((t) => t.slot === existing.slot);
                return t2 ? { ...existing, ...t2 } : existing;
              })
            : t2Data;
      }

      dataToProcess.sort((a, b) => a.slot - b.slot);
      let finalData =
        selectedBlock.length > 0
          ? dataToProcess.filter((item) =>
              selectedBlock.map((b) => b.value).includes(item.slot),
            )
          : dataToProcess;

      const finalDataWithDate = finalData.map((item: any) => ({
        ...item,
        date: dateLabel,
      }));
      setChartData(finalDataWithDate);
      setTableData(finalDataWithDate);
      setChartKeys([]);

      // ── Total Load & RVPN Drawal ─────────────────────────────────────────────
    } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const slotMap = new Map<number, any>();

      totalDiscomData.forEach((item) => {
        const slot = item.slot;
        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }
        const s = slotMap.get(slot);
        s.total_discom_min_drawal = (item.min_drawal || 0) * cf;
        s.total_discom_max_drawal = (item.max_drawal || 0) * cf;
        s.total_discom_avg_drawal = (item.avg_drawal || 0) * cf;
        s.total_discom_mode_drawal_window_formula =
          (item.mode_drawal_window_formula || 0) * cf;
        s.total_discom_date_of_min_drawal = item.date_of_min_drawal;
        s.total_discom_date_of_max_drawal = item.date_of_max_drawal;
      });

      if (selectedForecastType.includes("Actual") && isActualDates) {
        summaryData.forEach((item) => {
          const slot = item.slot;
          if (!slotMap.has(slot)) {
            slotMap.set(slot, {
              slot,
              slot_number: slot,
              slot_with_time:
                item.slot && item.time_string
                  ? `${item.slot} (${item.time_string})`
                  : item.slot,
            });
          }
          const s = slotMap.get(slot);
          s.rvpn_min_drawal = item.min_demand * cf;
          s.rvpn_max_drawal = item.max_demand * cf;
          s.rvpn_avg_drawal = item.avg_demand * cf;
          s.rvpn_mode_drawal_window_formula =
            item.mode_demand_window_formula * cf;
          s.rvpn_date_of_min_drawal = item.date_of_min;
          s.rvpn_date_of_max_drawal = item.date_of_max;
        });
      }

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      const finalData =
        selectedBlock.length > 0
          ? processedData.filter((item) =>
              selectedBlock.map((b) => b.value).includes(item.slot),
            )
          : processedData;

      const finalDataWithDate1 = finalData.map((item: any) => ({
        ...item,
        date: dateLabel,
      }));
      setChartData(finalDataWithDate1);
      setTableData(finalDataWithDate1);
      setChartKeys([
        "total_discom",
        ...(selectedForecastType.includes("Actual") && isActualDates
          ? ["rvpn"]
          : []),
      ]);

      // ── Total Discom Load ────────────────────────────────────────────────────
    } else if (selectedGroupKeys === "Total Discom Load") {
      const slotMap = new Map<number, any>();

      totalDiscomData.forEach((item) => {
        const slot = item.slot;
        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }
        const s = slotMap.get(slot);
        s.min_drawal = (item.min_drawal || 0) * cf;
        s.max_drawal = (item.max_drawal || 0) * cf;
        s.avg_drawal = (item.avg_drawal || 0) * cf;
        s.mode_drawal_window_formula =
          (item.mode_drawal_window_formula || 0) * cf;
        s.min_schedule = (item.min_schedule || 0) * cf;
        s.max_schedule = (item.max_schedule || 0) * cf;
        s.avg_schedule = (item.avg_schedule || 0) * cf;
        s.mode_schedule_window_formula =
          (item.mode_schedule_window_formula || 0) * cf;
        s.date_of_min_drawal = item.date_of_min_drawal;
        s.date_of_max_drawal = item.date_of_max_drawal;
        s.date_of_min_schedule = item.date_of_min_schedule;
        s.date_of_max_schedule = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      const finalData =
        selectedBlock.length > 0
          ? processedData.filter((item) =>
              selectedBlock.map((b) => b.value).includes(item.slot),
            )
          : processedData;

      const finalDataWithDate2 = finalData.map((item: any) => ({
        ...item,
        date: dateLabel,
      }));
      setChartData(finalDataWithDate2);
      setTableData(finalDataWithDate2);
      setChartKeys([]);

      // ── Discom Load ──────────────────────────────────────────────────────────
    } else {
      // ✅ DEMO: discom values are already "Discom 1/2/3" from DemoRestApi
      let filteredDiscomData = discomData;
      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredDiscomData = filteredDiscomData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }

      const slotMap = new Map<number, any>();

      filteredDiscomData.forEach((item) => {
        const slot = item.slot;
        const discom = item.discom;
        if (!slotMap.has(slot)) {
          slotMap.set(slot, {
            slot,
            slot_number: slot,
            slot_with_time:
              item.slot && item.time_string
                ? `${item.slot} (${item.time_string})`
                : item.slot,
          });
        }
        const s = slotMap.get(slot);
        s[`${discom}_min_drawal`] = item.min_drawal * cf;
        s[`${discom}_max_drawal`] = item.max_drawal * cf;
        s[`${discom}_avg_drawal`] = item.avg_drawal * cf;
        s[`${discom}_mode_drawal_window_formula`] =
          item.mode_drawal_window_formula * cf;
        s[`${discom}_min_schedule`] = item.min_schedule * cf;
        s[`${discom}_max_schedule`] = item.max_schedule * cf;
        s[`${discom}_avg_schedule`] = item.avg_schedule * cf;
        s[`${discom}_mode_schedule_window_formula`] =
          item.mode_schedule_window_formula * cf;
        s[`${discom}_date_of_min_drawal`] = item.date_of_min_drawal;
        s[`${discom}_date_of_max_drawal`] = item.date_of_max_drawal;
        s[`${discom}_date_of_min_schedule`] = item.date_of_min_schedule;
        s[`${discom}_date_of_max_schedule`] = item.date_of_max_schedule;
      });

      const processedData = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      const finalData =
        selectedBlock.length > 0
          ? processedData.filter((item) =>
              selectedBlock.map((b) => b.value).includes(item.slot),
            )
          : processedData;

      const finalDataWithDate3 = finalData.map((item: any) => ({
        ...item,
        date: dateLabel,
      }));
      setChartData(finalDataWithDate3);
      setTableData(finalDataWithDate3);
      setChartKeys(
        Array.from(
          new Set(filteredDiscomData.map((item) => item.discom)),
        ).sort(),
      );
    }
  }, [
    summaryData,
    discomData,
    totalDiscomData,
    forecastData,
    selectedUnit,
    selectedGroupKeys,
    selectedDiscom,
    selectedForecastType,
    selectedBlock,
    dateLabel,
    isActualDates,
    isForecastDates,
  ]);

  // ── Legends ────────────────────────────────────────────────────────────────
  const legends = useMemo(() => {
    const mkLegend = (
      dataKey: string,
      name: string,
      stackId: string,
      dateKey?: string,
    ) => ({
      dataKey,
      name,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId !== undefined ? stackId : undefined,
      ...(dateKey ? { dateKey } : {}),
    });

    if (selectedGroupKeys === "RVPN Drawal") {
      const result: any[] = [];
      if (selectedForecastType.includes("Actual") && isActualDates) {
        result.push(
          mkLegend(
            "min_drawal",
            "Min Drawal (Actual)",
            "drawal",
            "date_of_min_drawal",
          ),
          mkLegend(
            "max_drawal",
            "Max Drawal (Actual)",
            "drawal",
            "date_of_max_drawal",
          ),
          mkLegend("avg_drawal", "Average Drawal (Actual)", "drawal"),
          mkLegend(
            "mode_drawal_window_formula",
            "Mode Drawal (Actual)",
            "drawal",
          ),
        );
      }
      if (selectedForecastType.includes("Forecast D+1") && isForecastDates) {
        result.push(
          mkLegend(
            "min_drawal_t1",
            "Min Drawal (Forecast D+1)",
            "drawal_t1",
            "date_of_min_drawal_t1",
          ),
          mkLegend(
            "max_drawal_t1",
            "Max Drawal (Forecast D+1)",
            "drawal_t1",
            "date_of_max_drawal_t1",
          ),
          mkLegend(
            "avg_drawal_t1",
            "Average Drawal (Forecast D+1)",
            "drawal_t1",
          ),
          mkLegend(
            "mode_drawal_window_formula_t1",
            "Mode Drawal (Forecast D+1)",
            "drawal_t1",
          ),
        );
      }
      if (selectedForecastType.includes("Forecast D+2") && isForecastDates) {
        result.push(
          mkLegend(
            "min_drawal_t2",
            "Min Drawal (Forecast D+2)",
            "drawal_t2",
            "date_of_min_drawal_t2",
          ),
          mkLegend(
            "max_drawal_t2",
            "Max Drawal (Forecast D+2)",
            "drawal_t2",
            "date_of_max_drawal_t2",
          ),
          mkLegend(
            "avg_drawal_t2",
            "Average Drawal (Forecast D+2)",
            "drawal_t2",
          ),
          mkLegend(
            "mode_drawal_window_formula_t2",
            "Mode Drawal (Forecast D+2)",
            "drawal_t2",
          ),
        );
      }
      return result;
    }

    if (selectedGroupKeys === "Total Load & RVPN Drawal") {
      const metrics = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];
      return chartKeys.flatMap((key) =>
        metrics.map(({ field, displayName, hasDate }) => {
          const fieldSuffix =
            field === "mode" ? "mode_drawal_window_formula" : `${field}_drawal`;
          return {
            dataKey: `${key}_${fieldSuffix}`,
            name: `${key === "total_discom" ? "Total Discom" : "RVPN"} ${displayName} Drawal`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? "drawal" : undefined,
            dateKey: hasDate ? `${key}_date_of_${field}_drawal` : undefined,
          };
        }),
      );
    }

    if (selectedGroupKeys === "Total Discom Load") {
      const metrics = [
        { field: "min", displayName: "Min", hasDate: true },
        { field: "max", displayName: "Max", hasDate: true },
        { field: "avg", displayName: "Average", hasDate: false },
        { field: "mode", displayName: "Mode", hasDate: false },
      ];
      return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        metrics.map(({ field, displayName: metricLabel, hasDate }) => {
          const fieldSuffix =
            field === "mode"
              ? `mode_${suffix}_window_formula`
              : `${field}_${suffix}`;
          return {
            dataKey: fieldSuffix,
            name: `${metricLabel} ${displayName}`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? stackSuffix : undefined,
            dateKey: hasDate ? `date_of_${field}_${suffix}` : undefined,
          };
        }),
      );
    }

    // Discom Load
    const metrics = [
      { field: "min", displayName: "Min", hasDate: true },
      { field: "max", displayName: "Max", hasDate: true },
      { field: "avg", displayName: "Average", hasDate: false },
      { field: "mode", displayName: "Mode", hasDate: false },
    ];
    return chartKeys.flatMap((discom) =>
      selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
        metrics.map(({ field, displayName: metricLabel, hasDate }) => {
          const fieldSuffix =
            field === "mode"
              ? `mode_${suffix}_window_formula`
              : `${field}_${suffix}`;
          return {
            dataKey: `${discom}_${fieldSuffix}`,
            name: `${discom} ${metricLabel} ${displayName}`,
            type: selectedChartType.value,
            stackId:
              selectedChartType.stackId !== undefined ? stackSuffix : undefined,
            dateKey: hasDate
              ? `${discom}_date_of_${field}_${suffix}`
              : undefined,
          };
        }),
      ),
    );
  }, [
    selectedGroupKeys,
    selectedChartType,
    selectedForecastType,
    chartKeys,
    selectedSuffix,
    isActualDates,
    isForecastDates,
  ]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  // ── Custom tooltip ─────────────────────────────────────────────────────────
  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      let displaySeriesName = seriesName;

      if (selectedGroupKeys === "RVPN Drawal") {
        const map: Record<string, string> = {
          "Min Drawal (Actual)": dataPoint?.date_of_min_drawal,
          "Max Drawal (Actual)": dataPoint?.date_of_max_drawal,
          "Min Drawal (Forecast D+1)": dataPoint?.date_of_min_drawal_t1,
          "Max Drawal (Forecast D+1)": dataPoint?.date_of_max_drawal_t1,
          "Min Drawal (Forecast D+2)": dataPoint?.date_of_min_drawal_t2,
          "Max Drawal (Forecast D+2)": dataPoint?.date_of_max_drawal_t2,
        };
        if (map[seriesName])
          displaySeriesName = `${seriesName} (${map[seriesName]})`;
      } else if (selectedGroupKeys === "Total Load & RVPN Drawal") {
        if (seriesName.includes("Total Discom")) {
          if (
            seriesName.includes("Min Drawal") &&
            dataPoint?.total_discom_date_of_min_drawal
          )
            displaySeriesName = `${seriesName} (${dataPoint.total_discom_date_of_min_drawal})`;
          else if (
            seriesName.includes("Max Drawal") &&
            dataPoint?.total_discom_date_of_max_drawal
          )
            displaySeriesName = `${seriesName} (${dataPoint.total_discom_date_of_max_drawal})`;
        } else if (seriesName.includes("RVPN")) {
          if (
            seriesName.includes("Min Drawal") &&
            dataPoint?.rvpn_date_of_min_drawal
          )
            displaySeriesName = `${seriesName} (${dataPoint.rvpn_date_of_min_drawal})`;
          else if (
            seriesName.includes("Max Drawal") &&
            dataPoint?.rvpn_date_of_max_drawal
          )
            displaySeriesName = `${seriesName} (${dataPoint.rvpn_date_of_max_drawal})`;
        }
      } else if (selectedGroupKeys === "Total Discom Load") {
        const map: Record<string, string> = {
          "Min Schedule": dataPoint?.date_of_min_schedule,
          "Max Schedule": dataPoint?.date_of_max_schedule,
          "Min Drawal": dataPoint?.date_of_min_drawal,
          "Max Drawal": dataPoint?.date_of_max_drawal,
        };
        if (map[seriesName])
          displaySeriesName = `${seriesName} (${map[seriesName]})`;
      } else if (selectedGroupKeys === "Discom Load") {
        const discom = seriesName.split(" ")[0];
        const fieldMap: Record<string, string> = {
          "Min Schedule": `${discom}_date_of_min_schedule`,
          "Max Schedule": `${discom}_date_of_max_schedule`,
          "Min Drawal": `${discom}_date_of_min_drawal`,
          "Max Drawal": `${discom}_date_of_max_drawal`,
        };
        for (const [label, field] of Object.entries(fieldMap)) {
          if (seriesName.includes(label) && dataPoint?.[field]) {
            displaySeriesName = `${seriesName} (${dataPoint[field]})`;
            break;
          }
        }
      }

      return displaySeriesName;
    },
    [selectedGroupKeys],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleForecastTypeChange = (selected: string[]) => {
    setSelectedForecastType(selected.length === 0 ? ["Actual"] : selected);
    dispatch(setLoadingFilterName(""));
  };

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: ["slot_number", "date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Load Trend Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((m) => m.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Discom"
                items={DISCOM.map((d) => d.label)}
                selectedItems={selectedDiscom}
                onSelect={() => {}}
                setSelectedItems={(item) => {
                  if (selectedGroupKeys === "Total Discom Load") {
                    dispatch(setLoadingFilterName(""));
                    return;
                  }
                  onDiscomChange(item);
                }}
              />
            )}
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(_item, index) => onGroupByChange(index)}
          />
          {selectedGroupKeys === "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={FORECAST_TYPES}
              selectedItems={selectedForecastType}
              setSelectedItems={handleForecastTypeChange}
            />
          )}
          {selectedGroupKeys !== "RVPN Drawal" &&
            selectedGroupKeys !== "Total Load & RVPN Drawal" && (
              <FilterMenu
                title="Type"
                items={TYPE}
                selectedItems={selectedSuffix.map((s) => s?.displayName || "")}
                setSelectedItems={(selected) => onTypeChange(selected)}
              />
            )}
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((c) => c.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Discom Load Trend Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((b) => b.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    className="downloadButton"
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={true}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  resolveDisplayName={resolveTooltipName}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...tablePar} defaultSortDirection="asc" />
              )}
            </>
          )}
        </div>

        {/* ✅ DEMO: simplified footer — no Source tooltip, shows "Demo Version" */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
